import type {
  BuildingType,
  ResourceKind,
  ResourceStock,
  UnitType,
} from "./types";

export const MAP_SIZE = 120;
export const SIM_TICK_RATE = 10;
export const SNAPSHOT_RATE = 10;
export const MAX_PLAYERS = 2;
export const VISION_CELL_SIZE = 6;
export const TERRAIN_TILE_SIZE = 6;
export const NAVIGATION_CELL_SIZE = 1.5;

export type UnitCategory =
  | "Worker"
  | "Infantry"
  | "Archer"
  | "Cavalry"
  | "Building"
  | "Siege";

export interface UnitDefinition {
  label: string;
  description: string;
  lore: string;
  hp: number;
  speed: number;
  meleeAttack: number;
  rangedAttack: number;
  meleeArmour: number;
  rangedArmour: number;
  categories: UnitCategory[];
  attackBonuses: Partial<Record<UnitCategory, number>>;
  strongAgainst: string;
  weakAgainst: string;
  range: number;
  minimumRange?: number;
  splashRadius?: number;
  cooldown: number;
  vision: number;
  population: number;
  age: number;
  trainTime: number;
  cost: Partial<ResourceStock>;
}

export const UNIT_DEFS: Record<UnitType, UnitDefinition> = {
  villager: {
    label: "Villager",
    description: "Gathers resources and constructs buildings.",
    lore: "The hands that raise every hall and harvest every field.",
    hp: 40,
    speed: 4.1,
    meleeAttack: 3,
    rangedAttack: 0,
    meleeArmour: 0,
    rangedArmour: 0,
    categories: ["Worker"],
    attackBonuses: {},
    strongAgainst: "Economy and construction",
    weakAgainst: "All dedicated soldiers",
    range: 1.25,
    cooldown: 1.3,
    vision: 10,
    population: 1,
    age: 1,
    trainTime: 10,
    cost: { food: 50 },
  },
  scout: {
    label: "Mounted Scout",
    description: "Fast explorer and raider.",
    lore: "Light riders range ahead of the banners, trading armour for speed and sight.",
    hp: 85,
    speed: 7.2,
    meleeAttack: 7,
    rangedAttack: 0,
    meleeArmour: 0,
    rangedArmour: 0,
    categories: ["Cavalry"],
    attackBonuses: { Siege: 4 },
    strongAgainst: "Scouting, workers and exposed siege",
    weakAgainst: "Spearmen",
    range: 1.5,
    cooldown: 1.1,
    vision: 15,
    population: 1,
    age: 1,
    trainTime: 16,
    cost: { food: 70 },
  },
  spearman: {
    label: "Spearman",
    description: "Inexpensive infantry with a large cavalry bonus.",
    lore: "A disciplined hedge of ashwood spears can halt even the proudest charge.",
    hp: 70,
    speed: 3.7,
    meleeAttack: 5,
    rangedAttack: 0,
    meleeArmour: 1,
    rangedArmour: 0,
    categories: ["Infantry"],
    attackBonuses: { Cavalry: 8 },
    strongAgainst: "Cavalry",
    weakAgainst: "Archers and Royal Guards",
    range: 1.5,
    cooldown: 1.15,
    vision: 9,
    population: 1,
    age: 2,
    trainTime: 9,
    cost: { food: 60, wood: 25 },
  },
  archer: {
    label: "Longbow Archer",
    description: "Ranged infantry counter; vulnerable to cavalry.",
    lore: "Frontier bowyers train volleys that punish dense ranks before they can close.",
    hp: 48,
    speed: 3.8,
    meleeAttack: 0,
    rangedAttack: 5,
    meleeArmour: 0,
    rangedArmour: 1,
    categories: ["Archer"],
    attackBonuses: { Infantry: 5 },
    strongAgainst: "Infantry",
    weakAgainst: "Knights",
    range: 7.5,
    cooldown: 1.35,
    vision: 11,
    population: 1,
    age: 2,
    trainTime: 10,
    cost: { wood: 35, gold: 45 },
  },
  knight: {
    label: "Knight",
    description: "Powerful, mobile heavy cavalry.",
    lore: "Armoured household riders break bow lines with weight, speed, and cold steel.",
    hp: 150,
    speed: 5.7,
    meleeAttack: 12,
    rangedAttack: 0,
    meleeArmour: 3,
    rangedArmour: 2,
    categories: ["Cavalry"],
    attackBonuses: { Archer: 8, Siege: 8 },
    strongAgainst: "Archers and siege",
    weakAgainst: "Spearmen",
    range: 1.6,
    cooldown: 1.15,
    vision: 10,
    population: 1,
    age: 3,
    trainTime: 15,
    cost: { food: 90, gold: 70 },
  },
  skirmisher: {
    label: "Skirmisher",
    description: "Gold-free javelin soldier and anti-archer shield bearer.",
    lore: "A wicker shield turns arrows aside while a weighted javelin scatters the bow line.",
    hp: 45, speed: 3.8, meleeAttack: 0, rangedAttack: 2,
    meleeArmour: 0, rangedArmour: 3, categories: ["Archer"],
    attackBonuses: { Archer: 6 }, strongAgainst: "Archers", weakAgainst: "Cavalry and melee infantry",
    range: 6.5, cooldown: 1.6, vision: 10, population: 1, age: 2,
    trainTime: 12, cost: { food: 25, wood: 35 },
  },
  royal_guard: {
    label: "Royal Guard",
    description: "Elite castle infantry that breaks formations and structures.",
    lore: "Sworn at the castle gate, these veterans carry the lord's authority into the shield wall.",
    hp: 125,
    speed: 3.5,
    meleeAttack: 13,
    rangedAttack: 0,
    meleeArmour: 4,
    rangedArmour: 3,
    categories: ["Infantry"],
    attackBonuses: { Infantry: 5, Building: 6 },
    strongAgainst: "Infantry and buildings",
    weakAgainst: "Massed archers",
    range: 1.55,
    cooldown: 1.1,
    vision: 10,
    population: 1,
    age: 3,
    trainTime: 14,
    cost: { food: 80, gold: 50 },
  },
  bombard_cannon: {
    label: "Bombard Cannon",
    description: "Fragile long-range siege artillery built to shatter fortifications.",
    lore: "A thunderous powder engine whose iron shot can break masonry—but whose crew cannot survive a close assault.",
    hp: 90,
    speed: 2.35,
    meleeAttack: 0,
    rangedAttack: 7,
    meleeArmour: 0,
    rangedArmour: 0,
    categories: ["Siege"],
    attackBonuses: { Building: 24 },
    strongAgainst: "Buildings and fortifications",
    weakAgainst: "Infantry, archers, and cavalry",
    range: 15.5,
    cooldown: 3.4,
    vision: 16,
    population: 3,
    age: 4,
    trainTime: 26,
    cost: { wood: 175, gold: 225 },
  },
  onager: {
    label: "Onager",
    description: "Range 3–10.5 · 4s reload · Heavy, dodgeable splash hits enemies only.",
    lore: "A timber throwing arm sends stone into packed ranks; without an escort, its crew is easy prey.",
    hp: 55, speed: 2.2, meleeAttack: 0, rangedAttack: 40,
    meleeArmour: 0, rangedArmour: 0, categories: ["Siege"], attackBonuses: {},
    strongAgainst: "Clustered enemy armies", weakAgainst: "Cavalry and close assaults",
    range: 10.5, minimumRange: 3, splashRadius: 2.5, cooldown: 4, vision: 13,
    population: 3, age: 3, trainTime: 30, cost: { wood: 160, gold: 140 },
  },
};

export interface BuildingDefinition {
  label: string;
  description: string;
  hp: number;
  size: number;
  vision: number;
  age: number;
  buildTime: number;
  populationCap: number;
  cost: Partial<ResourceStock>;
  trains: UnitType[];
  garrisonCapacity: number;
  defensiveRange: number;
}

export const BUILDING_DEFS: Record<BuildingType, BuildingDefinition> = {
  camp: {
    label: "Supply Camp", description: "Deposit carried food, wood, gold and stone here.",
    hp: 450, size: 3, vision: 8, age: 1, buildTime: 15, populationCap: 0,
    cost: { wood: 100 }, trains: [], garrisonCapacity: 0, defensiveRange: 0,
  },
  town_center: {
    label: "Town Centre",
    description: "Economic heart, villager production, and age advancement.",
    hp: 1200,
    size: 5.2,
    vision: 14,
    age: 1,
    buildTime: 45,
    populationCap: 10,
    cost: { wood: 275, stone: 100 },
    trains: ["villager"],
    garrisonCapacity: 10,
    defensiveRange: 11,
  },
  house: {
    label: "House",
    description: "Increases population capacity by 10.",
    hp: 350,
    size: 2.8,
    vision: 7,
    age: 1,
    buildTime: 10,
    populationCap: 10,
    cost: { wood: 70 },
    trains: [],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  barracks: {
    label: "Barracks",
    description: "Trains spearmen, the counter to cavalry.",
    hp: 650,
    size: 4,
    vision: 8,
    age: 1,
    buildTime: 18,
    populationCap: 0,
    cost: { wood: 150 },
    trains: ["spearman"],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  archery_range: {
    label: "Archery Range",
    description: "Trains archers to defeat slow infantry.",
    hp: 600,
    size: 4,
    vision: 8,
    age: 2,
    buildTime: 20,
    populationCap: 0,
    cost: { wood: 175 },
    trains: ["archer", "skirmisher"],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  stable: {
    label: "Stable",
    description: "Trains mobile heavy cavalry.",
    hp: 700,
    size: 4.3,
    vision: 8,
    age: 2,
    buildTime: 22,
    populationCap: 0,
    cost: { wood: 175 },
    trains: ["scout", "knight"],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  tower: {
    label: "Watchtower",
    description: "Defensive fortification that fires on nearby enemies.",
    hp: 850,
    size: 2.5,
    vision: 15,
    age: 2,
    buildTime: 24,
    populationCap: 0,
    cost: { wood: 80, stone: 100 },
    trains: [],
    garrisonCapacity: 5,
    defensiveRange: 11,
  },
  wall: {
    label: "Palisade Wall",
    description: "A low-cost timber barrier that blocks movement and absorbs attacks.",
    hp: 450,
    size: 1.45,
    vision: 4,
    age: 1,
    buildTime: 5,
    populationCap: 0,
    cost: { wood: 20 },
    trains: [],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  gate: {
    label: "Palisade Gate",
    description: "Opens for friendly troops and briefly exposes a passage to enemies.",
    hp: 700,
    size: 3.4,
    vision: 5,
    age: 1,
    buildTime: 9,
    populationCap: 0,
    cost: { wood: 60 },
    trains: [],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  farm: {
    label: "Farm",
    description: "A renewable food source worked and reseeded by villagers.",
    hp: 300,
    size: 3.6,
    vision: 6,
    age: 1,
    buildTime: 9,
    populationCap: 0,
    cost: { wood: 60 },
    trains: [],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  market: {
    label: "Market",
    description: "Buys and sells food, wood, and stone for gold.",
    hp: 650,
    size: 4.2,
    vision: 9,
    age: 2,
    buildTime: 22,
    populationCap: 0,
    cost: { wood: 175 },
    trains: [],
    garrisonCapacity: 0,
    defensiveRange: 0,
  },
  castle: {
    label: "Castle",
    description: "A formidable stronghold that fires on enemies and trains elite and siege units.",
    hp: 2400,
    size: 6,
    vision: 18,
    age: 3,
    buildTime: 45,
    populationCap: 0,
    cost: { wood: 200, stone: 350 },
    trains: ["royal_guard", "bombard_cannon", "onager"],
    garrisonCapacity: 10,
    defensiveRange: 14,
  },
};

export const AGE_COSTS: Record<number, Partial<ResourceStock>> = {
  2: { food: 500, gold: 200 },
  3: { food: 800, gold: 500 },
  4: { food: 1000, gold: 800 },
};

export const AGE_NAMES = [
  "",
  "Settlement Age",
  "Feudal Age",
  "Castle Age",
  "Imperial Age",
];

export const RESOURCE_LABELS: Record<ResourceKind, string> = {
  food: "Food",
  wood: "Wood",
  gold: "Gold",
  stone: "Stone",
};

export function hasResources(
  stock: ResourceStock,
  cost: Partial<ResourceStock>,
): boolean {
  return (Object.keys(cost) as ResourceKind[]).every(
    (kind) => stock[kind] >= (cost[kind] ?? 0),
  );
}

export function spendResources(
  stock: ResourceStock,
  cost: Partial<ResourceStock>,
): void {
  for (const kind of Object.keys(cost) as ResourceKind[]) {
    stock[kind] -= cost[kind] ?? 0;
  }
}

export function formatCost(cost: Partial<ResourceStock>): string {
  return (Object.entries(cost) as [ResourceKind, number][])
    .map(([key, value]) => `${value} ${RESOURCE_LABELS[key].toLowerCase()}`)
    .join(" · ");
}
